import { useEffect, useMemo, useState } from 'react'
import { api } from '../api/client'
import type { Project, ScsItem } from '../types'
import { fmtMoney } from '../lib/format'
import ProgressBar from '../components/ProgressBar'

export default function BudgetTab({ project }: { project: Project }) {
  const [items, setItems] = useState<ScsItem[]>([])
  const [editing, setEditing] = useState<{ id: string; value: string } | null>(null)

  useEffect(() => {
    api.scs.list(project.id).then(setItems)
  }, [project.id])

  const totals = useMemo(() => {
    const budget = items.reduce((s, i) => s + i.budgeted_amount, 0)
    const drawn = items.reduce((s, i) => s + i.drawn_to_date, 0)
    const remaining = budget - drawn
    const contingency = items.find((i) => i.item.toLowerCase() === 'contingency')
    const contLeft = contingency ? contingency.budgeted_amount - contingency.drawn_to_date : 0
    return { budget, drawn, remaining, contLeft }
  }, [items])

  async function save(id: string, drawn: number) {
    const updated = await api.scs.update(id, { drawn_to_date: drawn })
    setItems((prev) => prev.map((x) => (x.id === id ? updated : x)))
    setEditing(null)
  }

  return (
    <section>
      <h2>Budget vs Drawn</h2>
      <p className="muted small mb-3">{items.length} SCS lines. Click a "Drawn" cell to edit.</p>

      <div className="stat-grid mb-4">
        <Stat label="Total Budget" value={fmtMoney(totals.budget)} />
        <Stat label="Drawn to Date" value={fmtMoney(totals.drawn)} />
        <Stat label="Balance" value={fmtMoney(totals.remaining)} />
        <Stat
          label="Contingency Left"
          value={fmtMoney(totals.contLeft)}
          alert={totals.contLeft < 40000}
        />
      </div>

      <div className="card no-pad">
        <div className="bud-grid bud-hdr">
          <div>#</div>
          <div>Item</div>
          <div>Sub / Type</div>
          <div className="text-right">Budget</div>
          <div className="text-right">Drawn</div>
          <div>%</div>
          <div className="text-right">Balance</div>
        </div>
        {items.map((i) => {
          const pct = i.budgeted_amount > 0 ? (i.drawn_to_date / i.budgeted_amount) * 100 : 0
          const balance = i.budgeted_amount - i.drawn_to_date
          return (
            <div key={i.id} className="bud-grid">
              <div className="muted small">{i.line_number}</div>
              <div className="bud-item">{i.item}</div>
              <div className="muted small">
                {i.sub_name ?? '—'}
                {i.allowance_or_contracted !== 'contracted' && (
                  <span className={`pill pill-${i.allowance_or_contracted}`}>
                    {i.allowance_or_contracted}
                  </span>
                )}
              </div>
              <div className="text-right">{fmtMoney(i.budgeted_amount)}</div>
              <div className="text-right editable">
                {editing?.id === i.id ? (
                  <div className="row gap-1" style={{ justifyContent: 'flex-end' }}>
                    <input
                      autoFocus
                      type="number"
                      value={editing.value}
                      onChange={(e) => setEditing({ id: i.id, value: e.target.value })}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') save(i.id, parseFloat(editing.value) || 0)
                        if (e.key === 'Escape') setEditing(null)
                      }}
                      className="cell-input"
                      style={{ width: 90 }}
                    />
                    <button
                      className="btn-ghost small"
                      title="Save (Enter)"
                      onClick={() => save(i.id, parseFloat(editing.value) || 0)}
                    >
                      ✓
                    </button>
                    <button
                      className="btn-ghost small"
                      title="Discard (Esc)"
                      onClick={() => setEditing(null)}
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <span onClick={() => setEditing({ id: i.id, value: String(i.drawn_to_date) })}>
                    {fmtMoney(i.drawn_to_date)}
                  </span>
                )}
              </div>
              <div>
                <ProgressBar pct={pct} height={4} />
              </div>
              <div className={`text-right ${balance < 0 ? 'danger' : ''}`}>{fmtMoney(balance)}</div>
            </div>
          )
        })}
      </div>
    </section>
  )
}

function Stat({ label, value, alert }: { label: string; value: string; alert?: boolean }) {
  return (
    <div className={`stat-card ${alert ? 'alert' : ''}`}>
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
    </div>
  )
}
